import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { authApi, notificationsApi, type NotificationRecord } from '@services/api';
import { useAuthStore } from '@store/authStore';
import { useThemeStore } from '@store/themeStore';
import { Bell, ChevronDown, KeyRound, Loader2, LogOut, Menu, Moon, Sun, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import { Loader } from '../ui/Loader';

import { usePageHeaderStore } from '@store/pageHeaderStore';

interface TopBarProps {
  onMobileMenuToggle?: () => void;
}

function getRouteHeaderInfo(pathname: string): { title: string; breadcrumb: string } {
  if (pathname === '/' || pathname === '/dashboard') {
    return { title: 'Dashboard', breadcrumb: 'Home / Dashboard' };
  }
  if (pathname === '/campaigns') {
    return { title: 'Phishing Simulation', breadcrumb: 'Home / Phishing Simulation' };
  }
  if (pathname === '/campaigns/templates') {
    return { title: 'Phishing Templates', breadcrumb: 'Home / Phishing Simulation / Phishing Templates' };
  }
  if (pathname === '/campaigns/create') {
    return { title: 'Create Campaign', breadcrumb: 'Home / Phishing Simulation / Create Campaign' };
  }
  if (pathname === '/campaigns/teachable-moments') {
    return { title: 'Teachable Moments', breadcrumb: 'Home / Phishing Simulation / Teachable Moments' };
  }
  if (pathname.startsWith('/campaigns/templates/')) {
    return { title: 'Template Detail', breadcrumb: 'Home / Phishing Simulation / Phishing Templates / Template Detail' };
  }
  if (pathname.startsWith('/campaigns/')) {
    return { title: 'Campaign Details', breadcrumb: 'Home / Phishing Simulation / Campaign Details' };
  }
  if (pathname === '/training') {
    return { title: 'Training', breadcrumb: 'Home / Training' };
  }
  if (pathname.startsWith('/training/assignments/')) {
    return { title: 'Assignment Details', breadcrumb: 'Home / Training / Assignment Details' };
  }
  if (pathname.startsWith('/training/modules/')) {
    return { title: 'Content Detail', breadcrumb: 'Home / Training / Content Detail' };
  }
  if (pathname.startsWith('/training/assessments/')) {
    return { title: 'Assessment', breadcrumb: 'Home / Training / Assessment' };
  }
  if (pathname === '/content') {
    return { title: 'Content Library', breadcrumb: 'Home / Content Library' };
  }
  if (pathname.startsWith('/content/')) {
    return { title: 'Content Details', breadcrumb: 'Home / Content Library / Content Details' };
  }
  if (pathname === '/users') {
    return { title: 'Users', breadcrumb: 'Home / Users' };
  }
  if (pathname === '/users/create') {
    return { title: 'Create User', breadcrumb: 'Home / Users / Create User' };
  }
  if (pathname.startsWith('/users/') && pathname.endsWith('/edit')) {
    return { title: 'Edit User', breadcrumb: 'Home / Users / Edit User' };
  }
  if (pathname.startsWith('/users/')) {
    return { title: 'User Details', breadcrumb: 'Home / Users / User Details' };
  }
  if (pathname === '/groups') {
    return { title: 'Groups', breadcrumb: 'Home / Groups' };
  }
  if (pathname === '/groups/create') {
    return { title: 'Create Group', breadcrumb: 'Home / Groups / Create Group' };
  }
  if (pathname.startsWith('/groups/')) {
    return { title: 'Group Details', breadcrumb: 'Home / Groups / Group Details' };
  }
  if (pathname === '/roles') {
    return { title: 'Roles', breadcrumb: 'Home / Roles' };
  }
  if (pathname === '/roles/create') {
    return { title: 'Create Role', breadcrumb: 'Home / Roles / Create Role' };
  }
  if (pathname.startsWith('/roles/') && pathname.endsWith('/edit')) {
    return { title: 'Edit Role', breadcrumb: 'Home / Roles / Edit Role' };
  }
  if (pathname === '/reports') {
    return { title: 'Reports & Analytics', breadcrumb: 'Home / Reports & Analytics' };
  }
  if (pathname.startsWith('/reports/')) {
    return { title: 'Report Viewer', breadcrumb: 'Home / Reports & Analytics / Report Viewer' };
  }
  if (pathname === '/settings') {
    return { title: 'Settings', breadcrumb: 'Home / Settings' };
  }
  if (pathname === '/profile') {
    return { title: 'User Profile', breadcrumb: 'Home / User Profile' };
  }
  if (pathname === '/admin/questions') {
    return { title: 'Question Bank', breadcrumb: 'Home / Training / Questions' };
  }
  if (pathname.startsWith('/admin/assessments/')) {
    return { title: 'Assessment Builder', breadcrumb: 'Home / Training / Assessment' };
  }

  const segment = pathname.split('/').filter(Boolean).pop() || 'Dashboard';
  const capitalized = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
  return { title: capitalized, breadcrumb: `Home / ${capitalized}` };
}

export function TopBar({ onMobileMenuToggle }: TopBarProps) {
  const { user, logout } = useAuthStore();
  const { isDark, toggle } = useThemeStore();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User';

  const routeInfo = getRouteHeaderInfo(location.pathname);

  const loadNotifications = async () => {
    try {
      const [listResponse, countResponse] = await Promise.all([
        notificationsApi.list({ limit: 5 }),
        notificationsApi.unreadCount(),
      ]);
      setNotifications(listResponse.data.data);
      setUnreadCount(countResponse.data.data.count);
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    if (user) {
      void loadNotifications();
    }
  }, [user?.id]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    const currentRefreshToken = useAuthStore.getState().refreshToken;
    await authApi.logout(currentRefreshToken ?? undefined).catch(() => null);
    logout();
    setIsLoggingOut(false);
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;

    setIsResettingPassword(true);
    try {
      await authApi.forgotPassword(user.email);
      toast({ type: 'success', message: 'Password reset link generated. Check your email.' });
    } catch (error: any) {
      toast({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Unable to generate reset link.',
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleMarkRead = async (notification: NotificationRecord) => {
    if (notification.isRead) return;

    await notificationsApi.markRead(notification.id);
    setNotifications((current) =>
      current.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item))
    );
    setUnreadCount((current) => Math.max(0, current - 1));
  };

  const handleMarkAllRead = async () => {
    if (!unreadCount) return;

    await notificationsApi.markAllRead();
    setNotifications((current) => current.map((item) => ({ ...item, isRead: true })));
    setUnreadCount(0);
  };

  const headerActions = usePageHeaderStore((s) => s.actions);

  return (
    <header className="h-16 bg-card border-b border-border flex items-center justify-between px-3 sm:px-4 md:px-6 shrink-0 w-full min-w-0">
      {/* Left section: Sidebar toggle + Dynamic Title & Breadcrumb (Seltrik Style) */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0 max-w-[50%] sm:max-w-none">
        {onMobileMenuToggle && (
          <button
            type="button"
            onClick={onMobileMenuToggle}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors md:hidden"
            aria-label="Open sidebar menu"
          >
            <Menu className="h-4 w-4" />
          </button>
        )}

        <div className="flex flex-col min-w-0">
          <h1 className="text-sm sm:text-base md:text-lg font-bold text-foreground leading-tight truncate tracking-tight">
            {routeInfo.title}
          </h1>
          <span className="hidden sm:inline text-[11px] font-medium text-muted-foreground truncate">
            {routeInfo.breadcrumb}
          </span>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {headerActions && (
          <div className="flex items-center gap-1.5 sm:gap-2 mr-0.5 sm:mr-1">
            {headerActions}
          </div>
        )}
        {/* Dark mode toggle */}
        <button
          onClick={toggle}
          className="p-1.5 sm:p-2 rounded-lg hover:bg-muted transition-colors shrink-0"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? (
            <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
          ) : (
            <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
          )}
        </button>

        {/* Notifications */}
        <DropdownMenu.Root onOpenChange={(open) => open && loadNotifications()}>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="relative p-1.5 sm:p-2 rounded-lg hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring shrink-0"
              aria-label="Open notifications"
            >
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              {unreadCount > 0 && (
                <span className="absolute right-1 top-1 min-w-4 rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-4 text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 w-96 rounded-lg border border-border bg-card p-1.5 shadow-xl"
            >
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-sm font-semibold text-foreground">Notifications</span>
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={!unreadCount}
                  className="text-xs font-medium text-brand-600 hover:text-brand-700 disabled:cursor-not-allowed disabled:text-muted-foreground"
                >
                  Mark all read
                </button>
              </div>

              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <div className="max-h-[300px] overflow-y-auto overscroll-contain pr-1">
                {notifications.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No notifications
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <DropdownMenu.Item
                      key={notification.id}
                      onSelect={() => handleMarkRead(notification)}
                      className="flex cursor-pointer gap-3 rounded-md px-3 py-2 text-sm outline-none hover:bg-muted focus:bg-muted"
                    >
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          notification.isRead ? 'bg-transparent' : 'bg-red-500'
                        }`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block whitespace-normal font-medium text-foreground">
                          {notification.title}
                        </span>
                        <span className="mt-0.5 line-clamp-4 whitespace-normal text-xs text-muted-foreground">
                          {notification.body}
                        </span>
                      </span>
                    </DropdownMenu.Item>
                  ))
                )}
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Open account menu"
            >
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={displayName}
                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="h-8 w-8 shrink-0 rounded-full bg-brand-500 flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
              )}
              <span className="hidden sm:block max-w-36 truncate text-sm font-medium text-foreground">
                {displayName}
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 min-w-56 rounded-lg border border-border bg-card p-1.5 shadow-xl"
            >
              <DropdownMenu.Item
                onSelect={() => navigate('/profile')}
                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted"
              >
                <User className="h-4 w-4 text-muted-foreground" />
                <span>Profile</span>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onSelect={handlePasswordReset}
                disabled={!user?.email || isResettingPassword}
                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
              >
                {isResettingPassword ? (
                  <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                )}
                <span>{isResettingPassword ? 'Generating...' : 'Reset Password'}</span>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item
                onSelect={handleLogout}
                disabled={isLoggingOut}
                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
              >
                {isLoggingOut ? (
                  <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                ) : (
                  <LogOut className="h-4 w-4 text-muted-foreground" />
                )}
                <span>Sign Out</span>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      {isLoggingOut && <Loader fullScreen message="Logging out securely..." />}
    </header>
  );
}
